import React from "react";
import Button from "../buttons/Button";
import ModalWrapper from "./ModalWrapper";

/**
 * ConfirmModal component renders a modal dialog with a confirmation message.
 *
 * @param {Object} props - The component props.
 * @param {boolean} props.isOpen - Determines if the modal is open.
 * @param {string} props.title - The title of the modal.
 * @param {Array} props.list - Array of text lines to display in the modal body.
 * @param {string} props.text - The body text message in the modal.
 * @param {React.ReactNode} props.children - Optional child components to render below the text.
 * @param {Function} props.onConfirm - Callback function to handle the confirm action.
 * @param {Function} [props.onSecondaryConfirm] - Optional callback for a middle action button.
 * @param {string} [props.cancelButtonText="Cancel"] - Label for the cancel button.
 * @param {Function} props.onClose - Callback function to handle the close action.
 * @param {boolean} props.retainDataOnClose - When true, keep form/children state when modal is closed (default: false).
 * @param {string} [props.overlayClassName] - Optional z-index/overlay class for ModalWrapper.
 * @returns {JSX.Element|null} The rendered modal component or null if not open (unless retainDataOnClose).
 */
const ConfirmModal = ({
  isOpen,
  title,
  list,
  text,
  textHTML,
  textAlign = "center",
  buttonText,
  cancelButtonText = "Cancel",
  secondaryButtonText,
  showCancel = true,
  confirmDisabled = false,
  secondaryDisabled = false,
  children = null,
  onConfirm,
  onSecondaryConfirm,
  onClose,
  retainDataOnClose = false,
  className = "",
  overlayClassName,
}) => {
  if (!isOpen && !retainDataOnClose) return null;

  return (
    <ModalWrapper
      isOpen={isOpen}
      onClose={onClose}
      retainDataOnClose={retainDataOnClose}
      overlayClassName={overlayClassName}
    >
      <div className={`flex justify-between items-center mb-4 ${textAlign === "center" ? "text-center" : "text-left"} ${className}`}>
        <h2 className="text-lg font-bold text-center w-full px-8 mr-6">
          {title}
        </h2>
      </div>
      <div className="text-center">
        {list && list.length > 0 && (
          <ul>
            {list.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        )}
        <p className={`m-4 ${textAlign === "center" ? "text-center" : "text-left"}`} dangerouslySetInnerHTML={{ __html: textHTML }}></p>
        <p className={`m-4 ${textAlign === "center" ? "text-center" : "text-left"}`}>{text}</p>

        {children && <div className="mt-4">{children}</div>}

        <div className="flex flex-row justify-between">
          {showCancel && (
            <Button
              onClick={onClose}
              text={cancelButtonText}
              variant="gray"
              className="mt-4 m-1 w-full mr-1"
            />
          )}
          {secondaryButtonText && onSecondaryConfirm && (
            <Button
              onClick={onSecondaryConfirm}
              text={secondaryButtonText}
              variant="primary"
              className="mt-4 m-1 w-full mx-1"
              disabled={secondaryDisabled}
            />
          )}
          <Button
            onClick={onConfirm}
            text={buttonText}
            variant="primary"
            className={`mt-4 m-1 w-full ${showCancel ? "ml-1" : "ml-0"}`}
            disabled={confirmDisabled}
          />
        </div>
      </div>
    </ModalWrapper>
  );
};

export default ConfirmModal;
